import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { EMPTY, Observable, of, timer } from 'rxjs';
import {
  catchError,
  expand,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { IResponseSuccess } from '@app/core/models/response-sucess.model';
import { ChannelAccommodationDownloadMultiPropertyResponse } from '@app/core/models/api/generics/channel-tools-response/channel-tools-response.interface';

import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { DownloadMappingChannelService } from '../../services/download-mapping-channel.service';

import * as fromActions from './actions';
import { effectHooks } from '../../helpers';

const DOWNLOAD_POLLING_INTERVAL_MS = 3000;

@Injectable()
export class DownloadMappingChannelStoreEffects {
  constructor(
    private dataService: DownloadMappingChannelService,
    private actions$: Actions,
    private errorHandler: ErrorHandlerService,
  ) {}

  downloadMultiProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.downloadRequest),
      switchMap(({ propertyIds, channelId, hooks }) =>
        this.dataService
          .downloadRoomsMultiProperty(propertyIds, channelId)
          .pipe(
            switchMap(({ data: { batch_job_id } }) =>
              this.pollDownloadJob(batch_job_id, channelId),
            ),
            takeUntil(this.actions$.pipe(ofType(fromActions.resetState))),
            tap((response) => {
              if (this.isDownloadJobOver(response.data)) {
                hooks?.onSuccess?.(response);
              }
            }),
            map((response) => {
              const { progress, pending, total } = response.data;

              return this.isDownloadJobOver(response.data)
                ? fromActions.downloadSuccess()
                : fromActions.downloadProgress({ progress, pending, total });
            }),
            catchError((error) => {
              this.errorHandler.handle(error);
              hooks?.onFailure?.(error);
              return of(fromActions.downloadFailure(error));
            }),
          ),
      ),
    ),
  );

  checkRoomsDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.checkRoomDeletedRequest),
      switchMap(({ propertyIds, channelId, hooks }) =>
        this.dataService.checkRoomsDeleted(propertyIds, channelId).pipe(
          effectHooks(hooks),
          map((response) => {
            return fromActions.checkRoomDeletedSuccess({
              data: response,
              propertyIds,
              channelId,
            });
          }),
          catchError((error) => {
            this.errorHandler.handle(error);
            return of(fromActions.checkRoomDeletedFailure(error));
          }),
        ),
      ),
    ),
  );

  private pollDownloadJob(
    batchJobId: string,
    channelId: number,
  ): Observable<
    IResponseSuccess<ChannelAccommodationDownloadMultiPropertyResponse>
  > {
    return this.dataService
      .downloadRoomsMultiPropertyCheckStatus(batchJobId, channelId)
      .pipe(
        expand(({ data }) =>
          this.isDownloadJobOver(data)
            ? EMPTY
            : timer(DOWNLOAD_POLLING_INTERVAL_MS).pipe(
                switchMap(() =>
                  this.dataService.downloadRoomsMultiPropertyCheckStatus(
                    batchJobId,
                    channelId,
                  ),
                ),
              ),
        ),
      );
  }

  private isDownloadJobOver(
    job: ChannelAccommodationDownloadMultiPropertyResponse,
  ) {
    return !!job?.finished || !!job?.cancelled;
  }
}
